// src/lib/invites.js
//
// Staff join the shop with an invite code. The code IS the secret: an invite
// document can be fetched by anyone who knows its id, but the ids are random
// and Firestore rules forbid listing the collection, so codes can't be
// enumerated or guessed.
//
// Pure helpers only — no Firestore in here, so the rules can be reasoned about
// and the format can be unit-tested.

// Ambiguous glyphs removed (0/O, 1/I/L) — these get read aloud over the phone
// and copied off a scrap of paper.
const ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';
const CODE_LEN = 10;

/** Cryptographically random invite code, e.g. "K7QF-3MRX-BD". */
export function generateInviteCode() {
  const bytes = new Uint32Array(CODE_LEN);
  crypto.getRandomValues(bytes);
  let out = '';
  for (let i = 0; i < CODE_LEN; i++) out += ALPHABET[bytes[i] % ALPHABET.length];
  return formatInviteCode(out);
}

/** Strip formatting and normalise case so "k7qf 3mrx-bd" matches "K7QF3MRXBD". */
export function normaliseInviteCode(input) {
  return String(input || '').toUpperCase().replace(/[^0-9A-Z]/g, '');
}

/** Group as XXXX-XXXX-XX for reading and typing. */
export function formatInviteCode(code) {
  const c = normaliseInviteCode(code);
  return [c.slice(0, 4), c.slice(4, 8), c.slice(8, 12)].filter(Boolean).join('-');
}

export function isWellFormedCode(input) {
  const c = normaliseInviteCode(input);
  return c.length === CODE_LEN && [...c].every(ch => ALPHABET.includes(ch));
}

export const INVITE_TTL_DAYS = 14;

export function inviteExpiryDate(from = new Date()) {
  const d = new Date(from);
  d.setDate(d.getDate() + INVITE_TTL_DAYS);
  return d;
}

const toDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);

/**
 * Why an invite can't be used. Returns null when it's good.
 * Kept separate from the UI so the same reasons can be tested directly.
 */
export function inviteProblem(invite, { email, now = new Date() } = {}) {
  if (!invite)        return 'That invite code is not valid. Check it with your manager.';
  if (invite.usedAt)  return 'That invite has already been used. Ask for a new one.';
  if (invite.revoked) return 'That invite was cancelled. Ask your manager for a new one.';

  const expires = toDate(invite.expiresAt);
  if (expires && expires < now) return 'That invite has expired. Ask your manager for a new one.';

  // The owner typed a specific address — binding to it means a leaked code
  // can't be redeemed by someone else.
  if (invite.email && email &&
      invite.email.trim().toLowerCase() !== email.trim().toLowerCase()) {
    return `That invite was issued to ${invite.email}. Sign up with that email address.`;
  }
  return null;
}

/** Shareable link that pre-fills the code on the join screen. */
export function inviteLink(code, origin = window.location.origin) {
  return `${origin}/?invite=${normaliseInviteCode(code)}`;
}

/** Pull a code out of the current URL, if the user arrived via an invite link. */
export function inviteCodeFromUrl(search = window.location.search) {
  const code = new URLSearchParams(search).get('invite');
  return code && isWellFormedCode(code) ? formatInviteCode(code) : '';
}
